/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import {
    Suspense,
    use,
    useCallback,
    useEffect,
    useState,
    type ComponentPropsWithoutRef,
    type ReactElement,
} from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperProducts } from '@/scapi';
import { WithCategoryNavigationMenu } from '@/components/navigation-menu';
import { DesktopNav } from '@/components/navigation/desktop-nav';
import { MobileNav } from '@/components/navigation/mobile-nav';
import { NavContentTile } from '@/components/navigation/nav-content-tile';
import type { NavContentEntries } from '@/lib/builder/nav-content.server';
import { Sheet } from '@/components/ui/sheet';
import { useHeaderOverlays } from '@/components/header/overlays';
import { navPreviewPath, type NavPreviewTarget } from '@/lib/navigation/preview-target';

/** The mega menu's breakpoint; below it the Builder preview opens the sheet instead. */
const DESKTOP_MEDIA_QUERY = '(min-width: 1024px)';

/** Nothing authored, nothing fetched yet, or Builder unreachable — the subnav is category-only. */
const NO_NAV_CONTENT: NavContentEntries = {};

type NavSurfacesProps = {
    categories: ShopperProducts.schemas['Category'][];
    tiles: NavContentEntries;
    apiKey: string;
    menuOpen: boolean;
    setMenuOpen: (next: boolean) => void;
    /** Builder preview only: the panel (and drawer) to hold open on both surfaces. */
    preview?: NavPreviewTarget;
};

/**
 * Both navigation surfaces, parameterised by the resolved tiles so the Suspense fallback can
 * render the identical tree with none.
 */
function NavSurfaces({ categories, tiles, apiKey, menuOpen, setMenuOpen, preview }: NavSurfacesProps): ReactElement {
    const { t } = useTranslation('header');

    const renderTile = (categoryId: string | undefined, name: string | undefined) => {
        const entry = categoryId ? tiles[categoryId] : undefined;
        // undefined, not a null-rendering element: the panel reads this to decide its column
        // count, and a rendered-null element is still truthy (SCO-NAV-03 D).
        if (!entry) return undefined;

        return (
            <NavContentTile
                entry={entry}
                apiKey={apiKey}
                label={t('featuredContent', {
                    category: name ?? '',
                    defaultValue: `${name ?? ''} featured content`,
                })}
            />
        );
    };

    return (
        <>
            {/* Desktop: mega menu (always rendered, hidden on mobile with CSS) */}
            <div className="hidden lg:flex items-center h-full">
                <DesktopNav
                    categories={categories}
                    renderContentSlot={(category) => renderTile(category.id, category.name)}
                    forcedOpen={preview}
                />
            </div>

            {/* Small: the full-screen navigation overlay (FSD 1.2), opened by the bottom bar's
                Shop bloc. */}
            <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                <MobileNav
                    categories={categories}
                    onNavigate={() => setMenuOpen(false)}
                    renderContentSlot={(category) => renderTile(category.id, category.name)}
                    initialPathIds={preview ? navPreviewPath(preview) : undefined}
                />
            </Sheet>
        </>
    );
}

function NavSurfacesWithTiles({
    navContent,
    ...surfaces
}: Omit<NavSurfacesProps, 'tiles'> & { navContent: Promise<NavContentEntries> }): ReactElement {
    return <NavSurfaces {...surfaces} tiles={use(navContent)} />;
}

interface ResponsiveNavigationMenuProps extends ComponentPropsWithoutRef<typeof WithCategoryNavigationMenu> {
    /**
     * Subnav content tiles by category id, deferred by the `_app` loader. Resolved up front, not
     * on hover, so the panel knows whether to reserve the fourth column before it renders.
     */
    navContent?: Promise<NavContentEntries>;
    /** Builder public API key; without one the tiles are skipped and the subnav is category-only. */
    builderApiKey?: string;
    /** Builder preview only (`/preview/nav-content`): the subnav to hold open. */
    preview?: NavPreviewTarget;
}

/**
 * One navigation structure for both breakpoints, switched by CSS rather than by rendering twice:
 * a hamburger overlay below 1024px, the horizontal mega menu above it.
 */
export default function ResponsiveNavigationMenu({
    resolve,
    defer,
    navContent,
    builderApiKey,
    preview,
}: ResponsiveNavigationMenuProps): ReactElement {
    // The bar's Shop bloc owns this overlay (SCO-NAV-01); standalone renders — the
    // stories — have no header above them, so they fall back to local state.
    const overlays = useHeaderOverlays();
    const [standaloneOpen, setStandaloneOpen] = useState(false);
    const menuOpen = overlays ? overlays.open === 'shop' : standaloneOpen;
    const setMenuOpen = useCallback(
        (next: boolean) => {
            if (overlays) {
                overlays.setOpen(next ? 'shop' : null);
                return;
            }
            setStandaloneOpen(next);
        },
        [overlays]
    );

    // The desktop panel is forced open by props; the Small sheet is an overlay, so it opens here.
    const previewCategoryId = preview?.categoryId;
    useEffect(() => {
        if (previewCategoryId && !window.matchMedia(DESKTOP_MEDIA_QUERY).matches) setMenuOpen(true);
    }, [previewCategoryId, setMenuOpen]);

    return (
        <WithCategoryNavigationMenu resolve={resolve} defer={defer}>
            {({ categories }) => {
                const surfaces = { categories, apiKey: builderApiKey ?? '', menuOpen, setMenuOpen, preview };

                if (!navContent || !builderApiKey) {
                    return <NavSurfaces {...surfaces} tiles={NO_NAV_CONTENT} />;
                }

                // The fallback is the same nav without tiles, so the bar is never missing while
                // the entries stream in.
                return (
                    <Suspense fallback={<NavSurfaces {...surfaces} tiles={NO_NAV_CONTENT} />}>
                        <NavSurfacesWithTiles {...surfaces} navContent={navContent} />
                    </Suspense>
                );
            }}
        </WithCategoryNavigationMenu>
    );
}
